import { Box, Button, Icon, Tooltip, Typography, useMediaQueryContext } from "@drone-js/core";
import { useUrlBuilder } from "apps/commons/hooks/useUrlBuilder";
import { Fragment, memo } from "react";
import { HeroContentLayout } from "./HeroContentLayout.tsx";
import { usePartnerMarketingModelValue } from "./partnerMarketingModelAtom";
import { useSetPassedPropertyWizardSection } from "./passedPropertyWizardSectionAtom";
import { useIntersectionObserver } from "./useIntersectionObserver";

interface HeroSectionSubtitle {
    text: string;
}

interface HeroSectionValues {
    text: string;
    tooltipText?: string;
}

interface HeroSectionSubtitleProps {
    subtitle: HeroSectionSubtitle;
}

const HeroSectionSubtitle = memo<HeroSectionSubtitleProps>(function HeroSectionSubtitle({ subtitle }) {
    const mediaQuery = useMediaQueryContext();
    return (
        <Typography variant={mediaQuery.m ? "body-default-strong" : "body-s-strong"} textColor="base.subtle">
            {subtitle?.text}
        </Typography>
    );
});

interface HeroSectionValueListProps {
    value: HeroSectionValues;
    length: number;
    index: number;
}

const HeroSectionValueList = memo<HeroSectionValueListProps>(({ value, index, length }) => {
    const setPassedPropertyWizardSection = useSetPassedPropertyWizardSection();
    const { componentRef: listPropertyButtonRef } = useIntersectionObserver(setPassedPropertyWizardSection);

    return (
        <Fragment>
            <Typography variant="body-default" ref={index === length - 1 ? listPropertyButtonRef : undefined}>
                {value?.text}
            </Typography>
            {value.tooltipText && (
                <Tooltip
                    content={value.tooltipText}
                    textAlign="start"
                    pointerPosition="below"
                    {...(index === 1
                        ? {
                              "data-element-name": "first-booking",
                          }
                        : {})}
                >
                    <Box display="flex" alignItems="center" pt="2">
                        <Icon id="line.symbol.circle-info" size="16" />
                    </Box>
                </Tooltip>
            )}
        </Fragment>
    );
});

export const HeroContent = memo(function HeroContent() {
    const {
        heroSectionViewModel: { subtitles, values, listAnyPropertyOnAgodaText, listMyPropertyText },
    } = usePartnerMarketingModelValue();
    const urlBuilder = useUrlBuilder();

    return (
        <HeroContentLayout
            subtitles={subtitles.map((subtitle) => (
                <HeroSectionSubtitle key={`pm-content-subtitle-${subtitle.text}`} subtitle={subtitle} />
            ))}
            title={
                <Typography variant="heading-3xl" textAlign="center">
                    {listAnyPropertyOnAgodaText}
                </Typography>
            }
            items={values.map((value, index) => (
                <Box display="flex" alignItems="center" gap="8" key={`pm-content-value-${index}`}>
                    <Icon id="fill.symbol.check" size="20" color="feedback.positive" />
                    <HeroSectionValueList value={value} length={values.length} index={index} />
                </Box>
            ))}
            button={
                <Button
                    hierarchy="primary"
                    variant="label"
                    data-element-name="list-your-property"
                    label={listMyPropertyText}
                    href={urlBuilder.onboardingUrl()}
                />
            }
        />
    );
});
